import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  ArrowLeft,
  ArrowRightToLine,
  AudioLines,
  ChevronDown,
  ImagePlus,
  Images,
  Loader2,
  Video,
  X,
  Check,
} from 'lucide-react';
import './CreateVideoPage.css';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import {
  getVideoModelSpecs,
  getCachedVideoModelSpecs,
  uploadStandaloneImage,
  uploadReferenceVideo,
  uploadSourceAudio,
  generateTextToVideo,
  generateImageToVideo,
  generateTransitionVideo,
  generateReferenceVideo,
  generateSeedance2Video,
  generateMotionControlVideo,
  remixVideo,
} from './lib/api';
import { prettyModelLabel } from './lib/videoUi';
import { estimateVideoCostUsd, formatUsd } from './lib/videoCostEstimate';
import { useVideoLibraryStore } from './stores';

const DEFAULT_TEXT_MODEL = 'gemini-omni-flash-text';
const MAX_REFERENCE_IMAGES = 4;
const SEEDANCE2_REFERENCE_FILE_LIMIT = 12;
const SEEDANCE25_REFERENCE_FILE_LIMIT = 50;
// Reference-only model served by its own endpoint (see generateReferenceVideo).
const KLING_O1_REFERENCE = 'kling-o1-reference';

const formatDurationLabel = (value) => {
  const label = String(value ?? '').trim();
  if (!label) return '5s';
  if (label.toLowerCase() === 'auto') return 'Auto';
  return /s$/i.test(label) ? label : `${label}s`;
};

const AspectRatioGlyph = ({ ratio }) => {
  const [rawWidth, rawHeight] = String(ratio).split(':').map(Number);
  const value = rawWidth > 0 && rawHeight > 0 ? rawWidth / rawHeight : 1;
  const maxWidth = 18;
  const maxHeight = 14;
  const boxRatio = maxWidth / maxHeight;
  const width = value >= boxRatio ? maxWidth : Math.max(6, maxHeight * value);
  const height = value >= boxRatio ? Math.max(6, maxWidth / value) : maxHeight;

  return (
    <span
      className="create-video-aspect-glyph"
      style={{ width: `${width}px`, height: `${height}px` }}
      aria-hidden="true"
    />
  );
};

const acceptsMax = (model, group, key) => {
  const groupObj = model?.accepts?.[group];
  const target = key ? groupObj?.[key] : groupObj;
  const value = Number(target?.max);
  return Number.isFinite(value) ? value : 0;
};

const acceptsMin = (model, group, key) => {
  const groupObj = model?.accepts?.[group];
  const target = key ? groupObj?.[key] : groupObj;
  const value = Number(target?.min);
  return Number.isFinite(value) ? value : 0;
};

/**
 * The image inputs a model supports, straight from its `accepts` spec.
 */
const modelInputSlots = (model) => {
  const referenceImageLimit = isSeedance25Model(model)
    ? 30 : isWan3ReferenceModel(model) ? 10 : isMiniMaxH3ReferenceModel(model) ? 9 : MAX_REFERENCE_IMAGES;
  const referenceMax = Math.min(referenceImageLimit, acceptsMax(model, 'images', 'reference'));
  const supportsMultimodalReferences = isMultimodalReferenceModel(model);
  const supportsMotionControl = isMotionControlModel(model);
  const supportsVideoEdit = isVideoEditModel(model);
  return {
    start: {
      max: acceptsMax(model, 'images', 'start_frame'),
      required: acceptsMin(model, 'images', 'start_frame') >= 1,
    },
    end: {
      max: acceptsMax(model, 'images', 'end_frame'),
      required: acceptsMin(model, 'images', 'end_frame') >= 1,
    },
    reference: {
      max: referenceMax,
      min: Math.min(referenceMax, acceptsMin(model, 'images', 'reference')),
    },
    videos: {
      max: supportsMotionControl || supportsVideoEdit
        ? Math.min(1, acceptsMax(model, 'videos'))
        : supportsMultimodalReferences ? Math.min(isSeedance25Model(model) ? 10 : isWan3ReferenceModel(model) ? 5 : 3, acceptsMax(model, 'videos')) : 0,
      min: supportsMotionControl || supportsVideoEdit
        ? Math.min(1, acceptsMin(model, 'videos'))
        : supportsMultimodalReferences ? Math.min(isSeedance25Model(model) ? 10 : isWan3ReferenceModel(model) ? 5 : 3, acceptsMin(model, 'videos')) : 0,
    },
    audios: {
      max: supportsMultimodalReferences ? Math.min(isSeedance25Model(model) ? 10 : isWan3ReferenceModel(model) ? 5 : 3, acceptsMax(model, 'audios')) : 0,
      min: supportsMultimodalReferences ? Math.min(isSeedance25Model(model) ? 10 : isWan3ReferenceModel(model) ? 5 : 3, acceptsMin(model, 'audios')) : 0,
    },
  };
};

const isSeedance2Model = (model) => {
  const alias = String(model?.alias || model?.id || '');
  return alias.startsWith('seedance2') || alias.startsWith('seedance_2.');
};

const isSeedance25Model = (model) => {
  const alias = String(model?.alias || model?.id || '');
  return alias.startsWith('seedance25') || alias.startsWith('seedance_2.5');
};

const multimodalReferenceFileLimit = (model) => (
  isSeedance25Model(model)
    ? SEEDANCE25_REFERENCE_FILE_LIMIT
    : isWan3ReferenceModel(model) ? 20 : SEEDANCE2_REFERENCE_FILE_LIMIT
);

const isMiniMaxH3Model = (model) => {
  const alias = String(model?.alias || model?.id || '');
  return alias === 'minimax-h3' || alias.startsWith('minimax-h3-');
};

const isMiniMaxH3ReferenceModel = (model) => {
  const alias = String(model?.alias || model?.id || '');
  return alias === 'minimax-h3-reference';
};

const isWan3Model = (model) => {
  const alias = String(model?.alias || model?.id || '');
  return alias.startsWith('wan30-');
};

const isWan3ReferenceModel = (model) => {
  const alias = String(model?.alias || model?.id || '');
  return alias === 'wan30-reference';
};

const isMultimodalReferenceModel = (model) =>
  isSeedance2Model(model) || isMiniMaxH3ReferenceModel(model) || isWan3ReferenceModel(model);

const isMotionControlModel = (model) => {
  const sourceInput = model?.sourceInput || model?.source_input || model?.params?.source_input?.default;
  return sourceInput === 'motion_control';
};

const isVideoEditModel = (model) =>
  ['klingo3-standard-edit', 'klingo3-pro-edit'].includes(String(model?.alias || model?.id || ''));

const isKlingO3EditFile = (file) => {
  const name = String(file?.name || '').toLowerCase();
  return ['video/mp4', 'video/quicktime'].includes(file?.type) || name.endsWith('.mp4') || name.endsWith('.mov');
};

const modelInputSummary = (model) => {
  if (isMotionControlModel(model)) return 'Character image · Motion video';
  if (isVideoEditModel(model)) return 'Source video · References ×4';
  const slots = modelInputSlots(model);
  const parts = [];
  if (slots.start.max >= 1) parts.push(slots.end.max >= 1 ? 'Start + end frame' : 'Start frame');
  if (slots.reference.max >= 1) parts.push(`References ×${slots.reference.max}`);
  if (slots.videos.max >= 1) parts.push(`Video refs ×${slots.videos.max}`);
  if (slots.audios.max >= 1) parts.push(`Audio refs ×${slots.audios.max}`);
  if (parts.length === 0) return 'Text to video';
  return parts.join(' · ');
};

/**
 * Minimal single-video creation flow: prompt in, video out.
 * The selected model decides which image inputs (start frame, end frame,
 * reference images) the composer offers.
 * Creates a standalone library video. Dedicated /videos creations never create
 * or attach themselves to a project.
 */
function CreateVideoPage() {
  const navigate = useNavigate();

  const [modelSpecs, setModelSpecs] = useState(() => getCachedVideoModelSpecs());
  const [prompt, setPrompt] = useState('');
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [modelSearch, setModelSearch] = useState('');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [duration, setDuration] = useState(null);
  const [resolution, setResolution] = useState('');
  const [characterOrientation, setCharacterOrientation] = useState('image');
  const [keepOriginalSound, setKeepOriginalSound] = useState(true);
  const [startFrame, setStartFrame] = useState(null); // { file, previewUrl }
  const [endFrame, setEndFrame] = useState(null); // { file, previewUrl }
  const [referenceImages, setReferenceImages] = useState([]); // [{ file, previewUrl }]
  const [referenceVideos, setReferenceVideos] = useState([]); // [{ file, previewUrl }]
  const [referenceAudios, setReferenceAudios] = useState([]); // [{ file, previewUrl }]
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const singleFileInputRef = useRef(null);
  const multiFileInputRef = useRef(null);
  const videoFileInputRef = useRef(null);
  const audioFileInputRef = useRef(null);
  const pendingSlotRef = useRef('start');
  const attachmentsRef = useRef([]);

  useEffect(() => {
    getVideoModelSpecs({ force: true })
      .then(setModelSpecs)
      .catch((err) => {
        console.error('Failed to fetch model specs:', err);
        setError('Failed to load video models. Please refresh and try again.');
      });
  }, []);

  useEffect(() => {
    attachmentsRef.current = [startFrame, endFrame, ...referenceImages, ...referenceVideos, ...referenceAudios].filter(Boolean);
  }, [startFrame, endFrame, referenceImages, referenceVideos, referenceAudios]);

  useEffect(() => {
    return () => {
      attachmentsRef.current.forEach((a) => URL.revokeObjectURL(a.previewUrl));
    };
  }, []);

  const eligibleModels = useMemo(() => {
    const models = Array.isArray(modelSpecs?.models) ? modelSpecs.models : [];
    return models.filter((m) => {
      if (m.agentOnly) return false;
      if (acceptsMax(m, 'prompts') < 1) return false;
      if ((acceptsMin(m, 'videos') > 0 || acceptsMin(m, 'audios') > 0)
        && !isMultimodalReferenceModel(m) && !isMotionControlModel(m) && !isVideoEditModel(m)) return false;
      // Reference-only models have no generation route here, except Kling O1
      // Reference which has a dedicated endpoint.
      const referenceOnly =
        acceptsMin(m, 'images', 'reference') >= 1 && acceptsMax(m, 'images', 'start_frame') < 1;
      if (referenceOnly && (m.alias || m.id) !== KLING_O1_REFERENCE
        && !isMultimodalReferenceModel(m) && !isMotionControlModel(m) && !isVideoEditModel(m)) return false;
      return true;
    });
  }, [modelSpecs]);

  const selectedModel = useMemo(() => {
    if (eligibleModels.length === 0) return null;
    const byId = (id) => eligibleModels.find((m) => (m.id || m.alias) === id);
    return byId(selectedModelId) || byId(DEFAULT_TEXT_MODEL) || eligibleModels[0];
  }, [eligibleModels, selectedModelId]);

  const filteredModels = useMemo(() => {
    const query = modelSearch.trim().toLowerCase();
    if (!query) return eligibleModels;
    return eligibleModels.filter((model) => {
      const label = prettyModelLabel(model.label, model.alias).toLowerCase();
      const summary = modelInputSummary(model).toLowerCase();
      return label.includes(query) || summary.includes(query);
    });
  }, [eligibleModels, modelSearch]);

  const slots = useMemo(() => modelInputSlots(selectedModel), [selectedModel]);
  const hasInputSlots = slots.start.max >= 1 || slots.end.max >= 1 || slots.reference.max >= 1
    || slots.videos.max >= 1 || slots.audios.max >= 1;
  const totalReferenceFiles = [startFrame, endFrame].filter(Boolean).length
    + referenceImages.length + referenceVideos.length + referenceAudios.length;

  const aspectOptions = useMemo(() => {
    const allowed = selectedModel?.allowedAspectRatios;
    if (Array.isArray(allowed) && allowed.length > 0) {
      return allowed.filter((r) => /^\d+:\d+$/.test(r));
    }
    return ['16:9', '9:16', '1:1'];
  }, [selectedModel]);

  const durationOptions = useMemo(() => {
    const allowed = selectedModel?.allowedDurationsSeconds;
    if (Array.isArray(allowed) && allowed.length > 0) return allowed;
    return ['5'];
  }, [selectedModel]);

  const resolutionOptions = useMemo(() => {
    const allowed = selectedModel?.allowedResolutions || selectedModel?.allowed_resolutions;
    if (Array.isArray(allowed) && allowed.length > 0) return allowed.filter(Boolean);

    const estimateRules = selectedModel?.estimatedCostRules
      || selectedModel?.estimated_cost_rules
      || selectedModel?.estimatedCostConfig
      || selectedModel?.estimated_cost_config;
    const rates = estimateRules?.ratesUsdPerSecond || estimateRules?.rates_usd_per_second;
    return rates && typeof rates === 'object' ? Object.keys(rates).filter(Boolean) : [];
  }, [selectedModel]);

  // Keep aspect/duration valid whenever the model (or its options) change.
  useEffect(() => {
    if (!aspectOptions.includes(aspectRatio)) {
      setAspectRatio(selectedModel?.defaultAspectRatio || aspectOptions[0]);
    }
  }, [aspectOptions, aspectRatio, selectedModel]);

  useEffect(() => {
    if (!duration || !durationOptions.includes(duration)) {
      setDuration(selectedModel?.defaultDurationSeconds || durationOptions[0]);
    }
  }, [durationOptions, duration, selectedModel]);

  useEffect(() => {
    if (resolutionOptions.length === 0) {
      setResolution('');
      return;
    }
    if (!resolutionOptions.includes(resolution)) {
      const preferred = selectedModel?.defaultResolution || selectedModel?.default_resolution;
      setResolution(resolutionOptions.includes(preferred) ? preferred : resolutionOptions[0]);
    }
  }, [resolutionOptions, resolution, selectedModel]);

  // This flow currently accepts image references only, so reference-video input
  // duration is zero. Keep it explicit because Seedance 2 pricing adds reference
  // video seconds to its resolution-specific output charge.
  const estimatedInputVideoSeconds = 0;
  const estimatedCostUsd = useMemo(() => estimateVideoCostUsd({
    modelSpec: selectedModel,
    durationSeconds: Number.parseFloat(duration),
    resolution,
    inputVideoSeconds: estimatedInputVideoSeconds,
  }), [duration, estimatedInputVideoSeconds, resolution, selectedModel]);
  const costEstimateLabel = estimatedCostUsd !== null
    ? `Estimated cost: ${formatUsd(estimatedCostUsd)}`
    : null;

  const openFilePicker = (slot) => {
    if (slot === 'reference') {
      multiFileInputRef.current?.click();
      return;
    }
    if (slot === 'video') {
      videoFileInputRef.current?.click();
      return;
    }
    if (slot === 'audio') {
      audioFileInputRef.current?.click();
      return;
    }
    pendingSlotRef.current = slot;
    singleFileInputRef.current?.click();
  };

  const handleSingleFile = (fileList) => {
    const file = Array.from(fileList || []).find((f) => f.type.startsWith('image/'));
    if (!file) return;
    const next = { file, previewUrl: URL.createObjectURL(file) };
    const setter = pendingSlotRef.current === 'end' ? setEndFrame : setStartFrame;
    setter((prev) => {
      if (prev) URL.revokeObjectURL(prev.previewUrl);
      return next;
    });
  };

  const handleReferenceFiles = (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (files.length === 0) return;
    setReferenceImages((prev) => {
      const next = [...prev];
      const combinedAvailable = Math.max(
        0,
        multimodalReferenceFileLimit(selectedModel) - referenceVideos.length - referenceAudios.length,
      );
      for (const file of files) {
        if (next.length >= slots.reference.max || next.length >= combinedAvailable) break;
        next.push({ file, previewUrl: URL.createObjectURL(file) });
      }
      return next;
    });
  };

  const handleMediaReferenceFiles = (fileList, kind, limit, setter) => {
    const files = Array.from(fileList || []).filter((file) => file.type.startsWith(`${kind}/`));
    if (files.length === 0) return;
    setter((prev) => [
      ...prev,
      ...files
        .slice(0, Math.max(0, Math.min(limit - prev.length, multimodalReferenceFileLimit(selectedModel) - totalReferenceFiles)))
        .map((file) => ({ file, previewUrl: URL.createObjectURL(file) })),
    ]);
  };

  const removeStartFrame = () => {
    setStartFrame((prev) => {
      if (prev) URL.revokeObjectURL(prev.previewUrl);
      return null;
    });
  };

  const removeEndFrame = () => {
    setEndFrame((prev) => {
      if (prev) URL.revokeObjectURL(prev.previewUrl);
      return null;
    });
  };

  const removeReferenceImage = (index) => {
    setReferenceImages((prev) => {
      const target = prev[index];
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const removeMediaReference = (setter, index) => {
    setter((prev) => {
      const target = prev[index];
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  // Why a model in the menu can't be selected with the current attachments.
  const modelBlockReason = (model) => {
    if (startFrame && acceptsMax(model, 'images', 'start_frame') < 1) {
      return 'Remove the start frame first';
    }
    if (endFrame && acceptsMax(model, 'images', 'end_frame') < 1) {
      return 'Remove the end frame first';
    }
    const modelSlots = modelInputSlots(model);
    const referenceCap = modelSlots.reference.max;
    if (referenceImages.length > referenceCap) {
      return referenceCap < 1
        ? 'Remove the reference images first'
        : `Takes at most ${referenceCap} reference image${referenceCap === 1 ? '' : 's'}`;
    }
    const videoCap = modelSlots.videos.max;
    if (referenceVideos.length > videoCap) return 'Remove the reference videos first';
    const audioCap = modelSlots.audios.max;
    if (referenceAudios.length > audioCap) return 'Remove the audio references first';
    const combinedLimit = multimodalReferenceFileLimit(model);
    if (isMultimodalReferenceModel(model) && totalReferenceFiles > combinedLimit) {
      return `Takes at most ${combinedLimit} reference files combined`;
    }
    return null;
  };

  const inputsBlocker = useMemo(() => {
    if (!selectedModel) return null;
    if (slots.start.required && !startFrame) return 'This model needs a start frame.';
    if (slots.end.required && !endFrame) return 'This model needs an end frame.';
    if (referenceImages.length < slots.reference.min) {
      return `This model needs at least ${slots.reference.min} reference image${slots.reference.min === 1 ? '' : 's'}.`;
    }
    if (referenceVideos.length < slots.videos.min) {
      return `This model needs at least ${slots.videos.min} reference video${slots.videos.min === 1 ? '' : 's'}.`;
    }
    if (referenceAudios.length < slots.audios.min) {
      return `This model needs at least ${slots.audios.min} audio reference${slots.audios.min === 1 ? '' : 's'}.`;
    }
    if (isVideoEditModel(selectedModel)) {
      const invalidFormat = referenceVideos.find((reference) => reference.file && !isKlingO3EditFile(reference.file));
      if (invalidFormat) return 'Kling O3 Edit accepts MP4 or MOV source videos.';
      const oversized = referenceVideos.find((reference) => Number(reference.file?.size || 0) > 200 * 1024 * 1024);
      if (oversized) return 'Kling O3 Edit source videos must be 200MB or smaller.';
    }
    if (endFrame && !startFrame) return 'Add a start frame to go with the end frame.';
    if (referenceImages.length > 0 && slots.start.max >= 1 && !startFrame) {
      return 'Reference images need a start frame with this model.';
    }
    if (isMultimodalReferenceModel(selectedModel) && referenceAudios.length > 0
      && !startFrame && referenceImages.length === 0 && referenceVideos.length === 0) {
      return 'Add an image or video when using an audio reference.';
    }
    const combinedLimit = multimodalReferenceFileLimit(selectedModel);
    if (isMultimodalReferenceModel(selectedModel) && totalReferenceFiles > combinedLimit) {
      return `This model accepts up to ${combinedLimit} reference files.`;
    }
    return null;
  }, [selectedModel, slots, startFrame, endFrame, referenceImages, referenceVideos, referenceAudios, totalReferenceFiles]);

  const handleSubmit = async () => {
    if (!prompt.trim() || isSubmitting || !selectedModel || inputsBlocker) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const requestId = Math.random().toString(36).substring(2, 15);

      const modelAlias = selectedModel.alias || selectedModel.id;
      const params = {
        prompt: prompt.trim(),
        model: modelAlias,
        duration: String(duration || '5'),
        aspectRatio,
        resolution: resolution || undefined,
      };

      const [startUrl, endUrl, referenceUrls, referenceVideoUploads, referenceAudioUrls] = await Promise.all([
        startFrame ? uploadStandaloneImage(startFrame.file) : null,
        endFrame ? uploadStandaloneImage(endFrame.file) : null,
        Promise.all(referenceImages.map((ref) => uploadStandaloneImage(ref.file))),
        Promise.all(referenceVideos.map(async (ref) => {
          const result = await uploadReferenceVideo(ref.file, null, isVideoEditModel(selectedModel));
          const url = result?.video_url || result?.videoUrl || result?.url;
          if (!url) throw new Error(`Could not upload ${ref.file.name || 'reference video'}.`);
          return {
            url,
            videoId: result?.videoId || result?.video_id,
            durationSeconds: result?.durationSeconds || result?.duration_seconds,
          };
        })),
        Promise.all(referenceAudios.map(async (ref) => {
          const result = await uploadSourceAudio(ref.file);
          const url = result?.audio_url || result?.audioUrl || result?.url;
          if (!url) throw new Error(`Could not upload ${ref.file.name || 'audio reference'}.`);
          return url;
        })),
      ]);
      const referenceVideoUrls = referenceVideoUploads.map((upload) => upload.url);
      const klingImages = referenceUrls.length > 0 ? referenceUrls : undefined;

      let generationPromise;
      if (isVideoEditModel(selectedModel)) {
        const sourceUpload = referenceVideoUploads[0];
        if (!sourceUpload?.videoId) throw new Error('Could not create the uploaded source video record.');
        generationPromise = remixVideo(sourceUpload.videoId, {
          prompt: params.prompt,
          model: modelAlias,
          imageUrls: referenceUrls,
          keepAudio: keepOriginalSound,
        });
      } else if (isMotionControlModel(selectedModel)) {
        generationPromise = generateMotionControlVideo(null, {
          prompt: params.prompt,
          model: modelAlias,
          imageUrl: referenceUrls[0],
          videoUrl: referenceVideoUrls[0],
          characterOrientation,
          keepOriginalSound,
        });
      } else if (isSeedance2Model(selectedModel) || isMiniMaxH3Model(selectedModel) || isWan3Model(selectedModel)) {
        const sourceImages = [startUrl, endUrl].filter(Boolean);
        generationPromise = generateSeedance2Video(null, {
          prompt: params.prompt,
          model: modelAlias,
          functionMode: sourceImages.length ? 'first_last_frames' : 'omni_reference',
          ratio: aspectRatio,
          duration: params.duration,
          resolution: resolution || undefined,
          imageFiles: sourceImages.length ? undefined : referenceUrls,
          videoFiles: referenceVideoUrls,
          audioFiles: referenceAudioUrls,
          filePaths: sourceImages.length ? sourceImages : undefined,
        });
      } else if (modelAlias === KLING_O1_REFERENCE) {
        generationPromise = generateReferenceVideo(null, {
          prompt: params.prompt,
          imageUrls: referenceUrls,
          duration: params.duration,
          aspectRatio,
        });
      } else if (startUrl && endUrl) {
        generationPromise = generateTransitionVideo(null, {
          ...params,
          firstImageUrl: startUrl,
          lastImageUrl: endUrl,
          resolution: resolution || selectedModel.defaultResolution || undefined,
          klingImages,
        });
      } else if (startUrl) {
        generationPromise = generateImageToVideo(null, { ...params, imageUrl: startUrl, klingImages });
      } else {
        generationPromise = generateTextToVideo(null, params);
      }

      // Some providers keep this request open until generation completes. Add a
      // client-side record now so the gallery can show the pending state at once.
      const optimisticId = `client-generating-${requestId}`;
      useVideoLibraryStore.getState().addLibraryVideo({
        id: optimisticId,
        projectId: null,
        projectName: null,
        prompt: params.prompt,
        status: 'generating',
        videoUrl: null,
        model: modelAlias,
        modelLabel: prettyModelLabel(selectedModel.label, modelAlias),
        aspectRatio,
        createdAt: new Date().toISOString(),
        isOptimistic: true,
      });
      generationPromise
        .then((response) => {
          const startedVideo = response?.video;
          if (!startedVideo?.id) return;

          // The API now acknowledges the durable background job immediately.
          // Replace the client-only placeholder with that real database row so
          // library polling follows the ID whose status will be finalized.
          const library = useVideoLibraryStore.getState();
          library.removeLibraryVideo(optimisticId);
          library.addLibraryVideo({
            ...startedVideo,
            projectId: null,
            projectName: null,
            durationMs: startedVideo.durationMs ?? startedVideo.duration,
            aspectRatio,
            model: startedVideo.model || modelAlias,
            modelLabel: prettyModelLabel(selectedModel.label, modelAlias),
            isOptimistic: false,
          });
        })
        .catch((err) => {
          console.error('Video generation failed:', err);
          useVideoLibraryStore.getState().updateLibraryVideo(optimisticId, {
            status: 'failed',
            errorMessage: 'Video generation failed.',
          });
        });
      navigate('/videos');
    } catch (err) {
      console.error('Failed to create video:', err);
      setError('Failed to start video generation. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const renderFilledTile = (attachment, label, onRemove, key, kind = 'image') => (
    <div key={key} className="create-video-tile">
      {kind === 'video' ? (
        <video src={attachment.previewUrl} muted preload="metadata" aria-label={label} />
      ) : kind === 'audio' ? (
        <span className="create-video-tile-audio" aria-label={label}><AudioLines size={20} /></span>
      ) : (
        <img src={attachment.previewUrl} alt={label} />
      )}
      <span className="create-video-tile-label">{label}</span>
      <button
        type="button"
        className="create-video-tile-remove"
        onClick={onRemove}
        disabled={isSubmitting}
        aria-label={`Remove ${label.toLowerCase()}`}
      >
        <X size={11} />
      </button>
    </div>
  );

  return (
    <AuthenticatedLayout>
      <div className="create-video-content">
        <button type="button" className="create-video-back" onClick={() => navigate('/videos')}>
          <ArrowLeft size={15} />
          Back to Videos
        </button>

        <div className="create-video-panel">
          <div className="create-video-heading">
            <h1 className="create-video-title">Create a video</h1>
            <p className="create-video-subtitle">Describe the idea. Fine-tune the setup.</p>
          </div>

          <div className="create-video-workspace">
            <div className="create-video-main">
              <div className="create-video-composer">
                <textarea
                  id="create-video-prompt"
                  className="create-video-textarea"
                  aria-label="Video prompt"
                  placeholder='e.g. "A golden retriever surfing a wave at sunset, cinematic slow motion"'
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={8}
                  disabled={isSubmitting}
                  autoFocus
                />
                <span className="create-video-prompt-meta">⌘ Enter to generate</span>
              </div>

              {error && <div className="create-video-error">{error}</div>}

              <p className="create-video-footnote">
                Your video will appear in the gallery while it generates.
              </p>
            </div>

            <aside className="create-video-settings" aria-label="Video settings">
              <section className="create-video-setting-section">
                <div className="create-video-setting-heading">
                  <span>Model</span>
                  <span>{eligibleModels.length} available</span>
                </div>
                <DropdownMenu.Root onOpenChange={(open) => !open && setModelSearch('')}>
                  <DropdownMenu.Trigger asChild>
                    <button type="button" className="create-video-model-trigger" disabled={isSubmitting}>
                      {selectedModel?.modelThumbnail && (
                        <img className="create-video-model-thumb" src={selectedModel.modelThumbnail} alt="" />
                      )}
                      <span className="create-video-model-trigger-copy">
                        <span className="create-video-model-label">
                          {selectedModel
                            ? prettyModelLabel(selectedModel.label, selectedModel.alias)
                            : 'Loading models…'}
                        </span>
                        <span className="create-video-model-summary">
                          {selectedModel ? modelInputSummary(selectedModel) : 'Fetching compatible models'}
                        </span>
                      </span>
                      <ChevronDown size={15} />
                    </button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Portal>
                    <DropdownMenu.Content className="create-video-menu create-video-menu--models" sideOffset={8} align="end">
                      <div className="create-video-model-search-wrap">
                        <input
                          className="create-video-model-search"
                          type="search"
                          placeholder="Search models or input type…"
                          value={modelSearch}
                          onChange={(e) => setModelSearch(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          aria-label="Search video models"
                        />
                        <span>{filteredModels.length} models</span>
                      </div>
                      <div className="create-video-model-list">
                        {filteredModels.map((model) => {
                          const id = model.id || model.alias;
                          const isSelected = selectedModel && (selectedModel.id || selectedModel.alias) === id;
                          const blockReason = modelBlockReason(model);
                          return (
                            <DropdownMenu.Item
                              key={id}
                              className={`create-video-menu-item create-video-menu-item--model${isSelected ? ' create-video-menu-item--selected' : ''}${blockReason ? ' create-video-menu-item--disabled' : ''}`}
                              disabled={Boolean(blockReason)}
                              onSelect={() => setSelectedModelId(id)}
                            >
                              {model.modelThumbnail ? (
                                <img className="create-video-model-thumb create-video-model-thumb--large" src={model.modelThumbnail} alt="" />
                              ) : (
                                <span className="create-video-model-thumb create-video-model-thumb--placeholder" />
                              )}
                              <div className="create-video-menu-item-copy">
                                <span className="create-video-menu-item-label">
                                  {prettyModelLabel(model.label, model.alias)}
                                </span>
                                <span className="create-video-menu-item-desc">
                                  {blockReason || modelInputSummary(model)}
                                </span>
                              </div>
                              {isSelected && <Check size={14} className="create-video-menu-check" />}
                            </DropdownMenu.Item>
                          );
                        })}
                        {filteredModels.length === 0 && (
                          <div className="create-video-menu-empty">No models match “{modelSearch}”</div>
                        )}
                      </div>
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
              </section>

              {isMotionControlModel(selectedModel) && (
                <>
                  <section className="create-video-setting-section">
                    <div className="create-video-setting-heading"><span>Character orientation</span></div>
                    <div className="create-video-aspect-group" role="group" aria-label="Character orientation">
                      {['image', 'video'].map((value) => (
                        <button
                          key={value}
                          type="button"
                          className={`create-video-aspect${characterOrientation === value ? ' create-video-aspect--active' : ''}`}
                          onClick={() => setCharacterOrientation(value)}
                          disabled={isSubmitting}
                        >
                          {value === 'image' ? 'Match image' : 'Match video'}
                        </button>
                      ))}
                    </div>
                    <p className="create-video-inputs-hint">
                      {characterOrientation === 'image'
                        ? 'Preserves the character image composition; best for camera movement.'
                        : 'Follows the reference framing; best for complex motion.'}
                    </p>
                  </section>
                  <section className="create-video-setting-section create-video-setting-section--row">
                    <div className="create-video-setting-heading"><span>Reference audio</span></div>
                    <label className="create-video-toggle">
                      <input
                        type="checkbox"
                        checked={keepOriginalSound}
                        onChange={(event) => setKeepOriginalSound(event.target.checked)}
                        disabled={isSubmitting}
                      />
                      <span>Keep original sound</span>
                    </label>
                  </section>
                </>
              )}

              {isVideoEditModel(selectedModel) && (
                <section className="create-video-setting-section create-video-setting-section--row">
                  <div className="create-video-setting-heading"><span>Source audio</span></div>
                  <label className="create-video-toggle">
                    <input
                      type="checkbox"
                      checked={keepOriginalSound}
                      onChange={(event) => setKeepOriginalSound(event.target.checked)}
                      disabled={isSubmitting}
                    />
                    <span>Keep source audio</span>
                  </label>
                </section>
              )}

              {hasInputSlots && (
                <section className="create-video-setting-section">
                  <div className="create-video-setting-heading">
                    <span>Inputs</span>
                    <span>{inputsBlocker ? 'Required' : 'Optional'}</span>
                  </div>
                  <div className="create-video-inputs">
                    {slots.start.max >= 1 &&
                      (startFrame ? (
                        renderFilledTile(startFrame, 'Start frame', removeStartFrame)
                      ) : (
                        <button
                          type="button"
                          className="create-video-tile create-video-tile--add"
                          onClick={() => openFilePicker('start')}
                          disabled={isSubmitting}
                        >
                          <ImagePlus size={16} />
                          <span>Start{slots.start.required && <em className="create-video-tile-required">*</em>}</span>
                        </button>
                      ))}
                    {slots.end.max >= 1 &&
                      (endFrame ? (
                        renderFilledTile(endFrame, 'End frame', removeEndFrame)
                      ) : (
                        <button
                          type="button"
                          className="create-video-tile create-video-tile--add"
                          onClick={() => openFilePicker('end')}
                          disabled={isSubmitting || !startFrame}
                        >
                          <ArrowRightToLine size={16} />
                          <span>End{slots.end.required && <em className="create-video-tile-required">*</em>}</span>
                        </button>
                      ))}
                    {slots.reference.max >= 1 && (
                      <>
                        {referenceImages.map((ref, index) =>
                          renderFilledTile(ref, `Reference ${index + 1}`, () => removeReferenceImage(index), ref.previewUrl)
                        )}
                        {referenceImages.length < slots.reference.max && (
                          <button
                            type="button"
                            className="create-video-tile create-video-tile--add"
                            onClick={() => openFilePicker('reference')}
                            disabled={isSubmitting}
                          >
                            <Images size={16} />
                            <span>{isMotionControlModel(selectedModel) ? 'Character' : 'Ref'} {referenceImages.length}/{slots.reference.max}</span>
                          </button>
                        )}
                      </>
                    )}
                    {referenceVideos.map((ref, index) =>
                      renderFilledTile(
                        ref,
                        `Video ${index + 1}`,
                        () => removeMediaReference(setReferenceVideos, index),
                        ref.previewUrl,
                        'video'
                      )
                    )}
                    {referenceVideos.length < slots.videos.max && (
                      <button
                        type="button"
                        className="create-video-tile create-video-tile--add"
                        onClick={() => openFilePicker('video')}
                        disabled={isSubmitting || totalReferenceFiles >= multimodalReferenceFileLimit(selectedModel)}
                      >
                        <Video size={16} />
                        <span>{isMotionControlModel(selectedModel) ? 'Motion' : isVideoEditModel(selectedModel) ? 'Source' : 'Video'} {referenceVideos.length}/{slots.videos.max}</span>
                      </button>
                    )}
                    {referenceAudios.map((ref, index) =>
                      renderFilledTile(
                        ref,
                        `Audio ${index + 1}`,
                        () => removeMediaReference(setReferenceAudios, index),
                        ref.previewUrl,
                        'audio'
                      )
                    )}
                    {referenceAudios.length < slots.audios.max && (
                      <button
                        type="button"
                        className="create-video-tile create-video-tile--add"
                        onClick={() => openFilePicker('audio')}
                        disabled={isSubmitting || totalReferenceFiles >= multimodalReferenceFileLimit(selectedModel)}
                      >
                        <AudioLines size={16} />
                        <span>Audio {referenceAudios.length}/{slots.audios.max}</span>
                      </button>
                    )}
                  </div>
                  {inputsBlocker && <p className="create-video-inputs-hint">{inputsBlocker}</p>}
                </section>
              )}

              {!isMotionControlModel(selectedModel) && !isVideoEditModel(selectedModel) && (
              <section className="create-video-setting-section">
                <div className="create-video-setting-heading"><span>Aspect ratio</span></div>
                <div
                  className="create-video-aspect-group"
                  role="group"
                  aria-label="Aspect ratio"
                  style={{ '--create-video-aspect-columns': Math.max(1, Math.min(aspectOptions.length, 3)) }}
                >
                  {aspectOptions.map((ratio) => {
                    return (
                      <button
                        key={ratio}
                        type="button"
                        className={`create-video-aspect${aspectRatio === ratio ? ' create-video-aspect--active' : ''}`}
                        onClick={() => setAspectRatio(ratio)}
                        disabled={isSubmitting}
                        title={ratio}
                      >
                        <AspectRatioGlyph ratio={ratio} />
                        {ratio}
                      </button>
                    );
                  })}
                </div>
              </section>
              )}

              {!isMotionControlModel(selectedModel) && !isVideoEditModel(selectedModel) && (
              <section className="create-video-setting-section create-video-setting-section--row">
                <div className="create-video-setting-heading"><span>Duration</span></div>
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <button type="button" className="create-video-chip" disabled={isSubmitting}>
                      {formatDurationLabel(duration)}
                      <ChevronDown size={13} />
                    </button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Portal>
                    <DropdownMenu.Content className="create-video-menu create-video-menu--duration" sideOffset={6}>
                      {durationOptions.map((seconds) => (
                        <DropdownMenu.Item
                          key={seconds}
                          className="create-video-menu-item"
                          onSelect={() => setDuration(seconds)}
                        >
                          <span>{formatDurationLabel(seconds)}</span>
                          {duration === seconds && <Check size={13} className="create-video-menu-check" />}
                        </DropdownMenu.Item>
                      ))}
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
              </section>
              )}

              {resolutionOptions.length > 0 && (
                <section className="create-video-setting-section create-video-setting-section--row">
                  <div className="create-video-setting-heading"><span>Resolution</span></div>
                  <select
                    className="create-video-select"
                    value={resolution}
                    disabled={isSubmitting}
                    onChange={(event) => setResolution(event.target.value)}
                    aria-label="Resolution"
                  >
                    {resolutionOptions.map((value) => (
                      <option key={value} value={value}>{value}</option>
                    ))}
                  </select>
                </section>
              )}

              {costEstimateLabel && (
                <div className="create-video-cost-estimate" aria-live="polite">
                  <span>{costEstimateLabel}</span>
                  <span>Final cost may vary</span>
                </div>
              )}

              <button
                type="button"
                className="create-video-submit"
                onClick={handleSubmit}
                disabled={isSubmitting || !prompt.trim() || !selectedModel || Boolean(inputsBlocker)}
              >
                {isSubmitting ? <Loader2 size={15} className="create-video-submit-spinner" /> : <ArrowRightToLine size={15} />}
                <span>{isSubmitting ? 'Starting…' : 'Generate video'}</span>
              </button>
            </aside>
          </div>

          <input ref={singleFileInputRef} type="file" accept="image/*" hidden onChange={(e) => { handleSingleFile(e.target.files); e.target.value = ''; }} />
          <input ref={multiFileInputRef} type="file" accept="image/*" multiple hidden onChange={(e) => { handleReferenceFiles(e.target.files); e.target.value = ''; }} />
          <input ref={videoFileInputRef} type="file" accept="video/*" multiple hidden onChange={(e) => { handleMediaReferenceFiles(e.target.files, 'video', slots.videos.max, setReferenceVideos); e.target.value = ''; }} />
          <input ref={audioFileInputRef} type="file" accept="audio/*" multiple hidden onChange={(e) => { handleMediaReferenceFiles(e.target.files, 'audio', slots.audios.max, setReferenceAudios); e.target.value = ''; }} />
        </div>
      </div>
    </AuthenticatedLayout>
  );
}

export default CreateVideoPage;
export { isMiniMaxH3Model, isMiniMaxH3ReferenceModel, isMotionControlModel, isMultimodalReferenceModel, isVideoEditModel, isWan3Model, modelInputSlots };
